import axios, { AxiosError, AxiosResponse } from 'axios';
import CryptoJS from 'crypto-js';
import { showNotification } from '../utils/notificationHandler';
import keycloak from '../auth/keycloak';
import { apm } from '../apm';

/*────────────────────────────────────────────
  1. AES encryption utilities (shared key/IV)
────────────────────────────────────────────*/
const isSecurityEnabled = false;
const KEY = CryptoJS.enc.Utf8.parse('mySecretAESKey12'); // 16-byte key
const IV = CryptoJS.enc.Utf8.parse('myInitVector1234');  // 16-byte IV

const encrypt = (payload: unknown): string => {
  const json = JSON.stringify(payload);
  return CryptoJS.AES.encrypt(json, KEY, {
    iv: IV,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  }).toString();
};

function decrypt<T = any>(cipher: string): T {
  const bytes = CryptoJS.AES.decrypt(cipher, KEY, {
    iv: IV,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });
  return JSON.parse(bytes.toString(CryptoJS.enc.Utf8)) as T;
}

/*────────────────────────────────────────────
  2. Error code and message structure
────────────────────────────────────────────*/
export enum ErrorCode {
  NETWORK = 0,
  BAD_REQUEST = 400,
  UNAUTHORIZED = 401,
  PAYMENT_REQUIRED = 402,
  FORBIDDEN = 403,
  INTERNAL_SERVER_ERROR = 500,
  SERVICE_UNAVAILABLE = 503,
}

export interface ErrorMeta {
  readonly code: ErrorCode;
  readonly defaultMessage: string;
}

const ERROR_META: Record<ErrorCode, ErrorMeta> = {
  [ErrorCode.NETWORK]: {
    code: ErrorCode.NETWORK,
    defaultMessage: 'Sunucuya şu anda ulaşılamıyor. Lütfen internet bağlantınızı kontrol edin ve tekrar deneyin.',
  },
  [ErrorCode.UNAUTHORIZED]: {
    code: ErrorCode.UNAUTHORIZED,
    defaultMessage: 'Oturum süreniz dolmuş olabilir. Lütfen yeniden giriş yapınız.',
  },
  [ErrorCode.FORBIDDEN]: {
    code: ErrorCode.FORBIDDEN,
    defaultMessage: 'Bu işlemi gerçekleştirmek için yetkiniz bulunmamaktadır.',
  },
  [ErrorCode.BAD_REQUEST]: {
    code: ErrorCode.BAD_REQUEST,
    defaultMessage: 'Gönderilen verilerde bir sorun oluştu. Lütfen bilgilerinizi kontrol ederek tekrar deneyin.',
  },
  [ErrorCode.PAYMENT_REQUIRED]: {
    code: ErrorCode.PAYMENT_REQUIRED,
    defaultMessage: 'Aboneliğiniz aktif değil veya ödeme gerekmektedir. Lütfen paket/ödeme bilgilerinizi kontrol ediniz.',
  },
  [ErrorCode.INTERNAL_SERVER_ERROR]: {
    code: ErrorCode.INTERNAL_SERVER_ERROR,
    defaultMessage: 'Sunucuda beklenmeyen bir hata oluştu. Lütfen daha sonra tekrar deneyin.',
  },
  [ErrorCode.SERVICE_UNAVAILABLE]: {
    code: ErrorCode.SERVICE_UNAVAILABLE,
    defaultMessage: 'Servis geçici olarak kullanılamıyor. Lütfen daha sonra tekrar deneyin.',
  },
};

const safeRedirect = (to: string) => {
  try {
    if (window.location.pathname !== to) {
      window.location.assign(to);
    }
  } catch {
    // ignore
  }
};

/*────────────────────────────────────────────
  3. Environment check (disable encryption on local/dev)
────────────────────────────────────────────*/
const isLocalEnv =
  process.env.REACT_APP_STAGE === 'local' ||
  process.env.NODE_ENV === 'development' ||
  ['localhost', '127.0.0.1'].includes(window.location.hostname) || false;

/*────────────────────────────────────────────
 4. Axios instance configuration
────────────────────────────────────────────*/

const API_BASE_URL = isLocalEnv ? 'http://localhost:8080/api/' : '/api'

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

/*────────────────────────────────────────────
  5. Request interceptor (encrypt request payload)
────────────────────────────────────────────*/
axiosInstance.interceptors.request.use(
  (config: import('axios').InternalAxiosRequestConfig): import('axios').InternalAxiosRequestConfig => {
    const method = (config.method ?? '').toLowerCase();

    const token = keycloak.token;
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    if (isSecurityEnabled && !isLocalEnv && ['post', 'put', 'patch'].includes(method) && config.data) {
      config.data = { data: encrypt(config.data) };
    }

    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

/*────────────────────────────────────────────
  6. Response interceptor (decrypt response and handle errors globally)
────────────────────────────────────────────*/
axiosInstance.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => {
    if (isSecurityEnabled && !isLocalEnv && response.data?.data) {
      response.data = decrypt(response.data.data);
    }
    return response;
  },
  (error: AxiosError) => {
    const requestUrl = error.config?.url ?? 'unknown-url';
    const requestMethod = (error.config?.method ?? 'UNKNOWN').toUpperCase();
    const apmError = new Error(`HTTP ${requestMethod} ${requestUrl} failed with status ${error.response?.status ?? 0}`);

    apm.captureError(apmError, {
      labels: {
        http_status: String(error.response?.status ?? 0),
        http_method: requestMethod,
        request_url: requestUrl,
        api_base_url: API_BASE_URL,
        response_code: String((error.response?.data as { code?: string } | undefined)?.code ?? ''),
        base_url: String(error.config?.baseURL ?? ''),
        full_url: String(`${error.config?.baseURL ?? ''}${error.config?.url ?? ''}`),
      },
    });
    const status = (error.response?.status ?? 0) as ErrorCode;
    const meta = ERROR_META[status] ?? ERROR_META[ErrorCode.NETWORK];
    let errorMessage = meta.defaultMessage;

    // Attempt to extract detailed error message (if AES encrypted)
    const response = error.response as AxiosResponse<{ code: string, message: string, isScreenError?: boolean }>;
    
    if(status == ErrorCode.UNAUTHORIZED && response?.data && response?.data.code === 'UNAUTHORIZED' && response?.data.isScreenError){
      keycloak.logout({ redirectUri: window.location.origin })
    }

    if (status === ErrorCode.PAYMENT_REQUIRED && response?.data && response?.data.code === 'SUBSCRIPTION_INACTIVE' ) {
      const resultUrl = new URL('/subscription/result', window.location.origin);

      resultUrl.searchParams.set('code', 'SUBSCRIPTION_INACTIVE');
      resultUrl.searchParams.set('status', String(status));

      safeRedirect(`${resultUrl.pathname}${resultUrl.search}`);
      return Promise.reject(error);
    }

    if (
      (status === ErrorCode.BAD_REQUEST || status === ErrorCode.INTERNAL_SERVER_ERROR) &&
      response?.data &&
      response?.data.isScreenError
    ) {
      try {
        if (response?.data?.message) {
          errorMessage = response.data.message.replaceAll('\n','<br/>');
        }
      } catch {
        // ignore parse errors
      }
    }

    showNotification('error', 'Hata oluştu',errorMessage);
    return Promise.reject(error);
  },
);

export default axiosInstance;